// A formatted value cut into two runs that each stay whole (design-system
// §3.3, `fit`, D-336): a timestamp's date and time (lib/datetime.ts), an
// amount and its currency (areas/office/sales/lib/money.ts). The cut is made
// in the formatter's own string, so `first + gap + second` is exactly the text
// the value renders as in one piece, and `gap` is that text's own whitespace.
// A value that cannot be cut honestly comes back as one run, with `gap` and
// `second` empty. `WholeRuns` (components/whole-runs.tsx) renders the runs.

export interface TwoRuns {
  readonly first: string;
  readonly gap: string;
  readonly second: string;
}

/** The value as one run. */
export function oneRun(text: string): TwoRuns {
  return { first: text, gap: "", second: "" };
}

/**
 * Cut `whole` before the second run, which starts at `at`. `parts` are the
 * formatter's `formatToParts` values, used only to locate the cut: V8's
 * `format` writes U+0020 where `formatToParts` keeps ICU's no-break spaces
 * (ru «2025 г.»), so the two may differ in their spaces and in nothing else,
 * and any other drift returns `whole` as one run. The second run must be
 * preceded by whitespace and the first must hold something else.
 */
export function cutBefore(whole: string, parts: readonly string[], at: number): TwoRuns {
  const joined = parts.join("");
  const spaced = (text: string): string => text.replace(/\s/gu, " ");
  if (joined.length !== whole.length || spaced(joined) !== spaced(whole)) return oneRun(whole);
  if (at <= 0 || at >= whole.length) return oneRun(whole);
  const head = whole.slice(0, at);
  const match = /^(.*\S)(\s+)$/su.exec(head);
  if (match === null) return oneRun(whole);
  const first = match[1] ?? "";
  return { first, gap: head.slice(first.length), second: whole.slice(at) };
}
